// Phase 14.7 — Admin panel for orphaned and mismatched appointments.
//
// Buckets surfaced:
//   null_agent      — agent_id IS NULL (~21 rows from ETL)
//   maker_mismatch  — agent A, client owned by maker B (~51 rows)
//
// Admin selects N rows + picks a target agent + Zuweisen → bulk update.
// Buckets are computed via /appointments/admin/buckets; the actual
// table content comes from /appointments?limit=500 client-side
// filtered to the active tab.

import { createFileRoute, redirect } from '@tanstack/react-router';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Users, AlertCircle, CheckSquare, Square, UserPlus, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Avatar } from '@/components/ui/avatar';
import { SearchablePicker } from '@/components/ui/SearchablePicker';
import { useAppointments, type AppointmentRow } from '@/hooks/data/useAppointments';
import { useClients } from '@/hooks/data/useClients';
import { useAgents } from '@/hooks/data/useAgents';
import {
  useAppointmentBuckets,
  useBulkAssignAppointments,
} from '@/hooks/data/useAppointmentOrphans';
import { fetchMe } from '@/lib/auth';
import { formatDateTime } from '@/lib/formatLocale';

export const Route = createFileRoute('/_app/admin/appointments-orphans')({
  beforeLoad: async () => {
    const me = await fetchMe();
    if (!me || me.profile?.role !== 'admin') {
      throw redirect({ to: '/dashboard' });
    }
  },
  component: OrphansPage,
});

type Bucket = 'null_agent' | 'maker_mismatch';

function OrphansPage() {
  const { t } = useTranslation();
  const [bucket, setBucket] = useState<Bucket>('null_agent');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [targetAgent, setTargetAgent] = useState<string>('');

  const { data: appointments = [] } = useAppointments({ limit: 10000 });
  const { data: clients = [] } = useClients({ limit: 10000 });
  const { data: agents = [] } = useAgents();
  const { data: buckets = [] } = useAppointmentBuckets();
  const bulkAssign = useBulkAssignAppointments();

  const clientMap = useMemo(() => new Map(clients.map((c) => [c.id, c])), [clients]);
  const profileMap = useMemo(() => new Map(agents.map((a) => [a.id, a])), [agents]);

  const filtered = useMemo(() => {
    return appointments.filter((a) => {
      if (bucket === 'null_agent') return a.agent_id == null;
      if (bucket === 'maker_mismatch') {
        if (!a.agent_id) return false;
        const c = a.client_id ? clientMap.get(a.client_id) : null;
        if (!c?.assigned_agent_id) return false;
        if (c.assigned_agent_id === a.agent_id) return false;
        // Both must be makers for "mismatch" tab; admin/CC is the other bucket.
        const apptAgent = profileMap.get(a.agent_id);
        const clientOwner = profileMap.get(c.assigned_agent_id);
        return apptAgent?.role === 'makler' && clientOwner?.role === 'makler';
      }
      return false;
    });
  }, [appointments, bucket, clientMap, profileMap]);

  // Sort newest first
  const sorted = useMemo(
    () => [...filtered].sort((a, b) => b.appointment_date - a.appointment_date),
    [filtered],
  );

  const bucketCount = (key: string) =>
    buckets.find((b) => b.bucket === key)?.n ?? 0;

  const allSelected = sorted.length > 0 && sorted.every((a) => selectedIds.has(a.id));
  const toggleAll = () => {
    if (allSelected) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(sorted.map((a) => a.id)));
    }
  };
  const toggleOne = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const submit = async () => {
    if (!targetAgent || selectedIds.size === 0) return;
    try {
      const res = await bulkAssign.mutateAsync({
        ids: Array.from(selectedIds),
        agent_profile_id: targetAgent,
      });
      const agent = profileMap.get(targetAgent);
      toast.success(
        t('orphans.assigned', '{{count}} Termine an {{name}} zugewiesen', {
          count: res.count,
          name: agent?.full_name ?? '?',
        }),
      );
      setSelectedIds(new Set());
      setTargetAgent('');
    } catch (err) {
      toast.error((err as Error).message);
    }
  };

  return (
    <section className="space-y-6">
      <header>
        <h2 className="font-display text-2xl text-ink">
          {t('orphans.title', 'Nicht zugewiesene Termine')}
        </h2>
        <p className="text-sm text-ink-3">
          {t(
            'orphans.subtitle',
            'Termine ohne Agent oder mit fehlerhafter Zuordnung. Nur Admin.',
          )}
        </p>
      </header>

      <div className="fm-card p-5 space-y-4">
        <div className="flex flex-wrap items-center gap-2">
          <BucketTab
            active={bucket === 'null_agent'}
            count={bucketCount('null_agent')}
            label={t('orphans.tab.null_agent', 'Ohne Agent')}
            icon={<AlertCircle className="h-3.5 w-3.5" />}
            onClick={() => {
              setBucket('null_agent');
              setSelectedIds(new Set());
            }}
          />
          <BucketTab
            active={bucket === 'maker_mismatch'}
            count={bucketCount('maker_mismatch')}
            label={t('orphans.tab.maker_mismatch', 'Makler-Diskrepanz')}
            icon={<Users className="h-3.5 w-3.5" />}
            onClick={() => {
              setBucket('maker_mismatch');
              setSelectedIds(new Set());
            }}
          />
        </div>

        {sorted.length === 0 ? (
          <p className="py-10 text-center text-sm text-ink-3">
            {t('orphans.empty', 'Keine Termine in dieser Kategorie.')}
          </p>
        ) : (
          <div className="-mx-5 overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-y border-line bg-paper-2 text-[11px] font-semibold uppercase tracking-wider text-ink-3">
                  <th className="px-5 py-2 w-10">
                    <button onClick={toggleAll} className="inline-flex">
                      {allSelected ? (
                        <CheckSquare className="h-4 w-4 text-ink" />
                      ) : (
                        <Square className="h-4 w-4 text-ink-3" />
                      )}
                    </button>
                  </th>
                  <th className="px-2 py-2 text-start">{t('orphans.col.date', 'Datum')}</th>
                  <th className="px-2 py-2 text-start">{t('orphans.col.type', 'Typ')}</th>
                  <th className="px-2 py-2 text-start">{t('orphans.col.client', 'Klient')}</th>
                  {bucket === 'maker_mismatch' && (
                    <>
                      <th className="px-2 py-2 text-start">{t('orphans.col.appt_agent', 'Termin-Agent')}</th>
                      <th className="px-2 py-2 text-start">{t('orphans.col.client_owner', 'Klient-Besitzer')}</th>
                    </>
                  )}
                  <th className="px-2 py-2 text-start">{t('orphans.col.notes', 'Notiz')}</th>
                </tr>
              </thead>
              <tbody>
                {sorted.map((a) => (
                  <OrphanRow
                    key={a.id}
                    appt={a}
                    bucket={bucket}
                    selected={selectedIds.has(a.id)}
                    onToggle={() => toggleOne(a.id)}
                    clientMap={clientMap}
                    profileMap={profileMap}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Bulk action bar — fixed bottom, shown when selection > 0 */}
      {selectedIds.size > 0 && (
        <div className="fixed inset-x-0 bottom-0 z-30 border-t border-line bg-card shadow-elevated md:bottom-6 md:left-1/2 md:right-auto md:-translate-x-1/2 md:rounded-2xl md:border md:max-w-2xl">
          <div className="flex flex-wrap items-center gap-2 px-4 py-3">
            <span className="text-sm font-medium text-ink">
              {selectedIds.size} {t('orphans.selected', 'ausgewählt')}
            </span>
            <span className="mx-1 h-4 w-px bg-line" />
            <div className="min-w-[220px] flex-1">
              <SearchablePicker
                value={targetAgent}
                onChange={setTargetAgent}
                placeholder={t('orphans.pick_agent', 'Makler wählen …')}
                options={agents
                  .filter((a) => a.status === 'active')
                  .map((a) => ({ value: a.id, label: a.full_name, secondary: a.agent_id }))}
                emptyText={t('common.no_matches', 'Keine Treffer')}
              />
            </div>
            <Button onClick={submit} disabled={!targetAgent || bulkAssign.isPending} size="sm">
              <UserPlus className="me-1.5 h-3.5 w-3.5" />
              {bulkAssign.isPending
                ? t('orphans.assigning', 'Wird zugewiesen …')
                : t('orphans.assign', 'Zuweisen')}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setSelectedIds(new Set())}
              type="button"
            >
              <X className="h-3.5 w-3.5" />
            </Button>
          </div>
        </div>
      )}
    </section>
  );
}

function BucketTab({
  active,
  count,
  label,
  icon,
  onClick,
}: {
  active: boolean;
  count: number;
  label: string;
  icon: React.ReactNode;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`inline-flex items-center gap-2 rounded-md border px-3 py-1.5 text-xs font-semibold transition ${
        active
          ? 'border-ink bg-ink text-paper'
          : 'border-line bg-card text-ink-2 hover:bg-paper-2'
      }`}
    >
      {icon}
      {label}
      <span
        className={`rounded-full px-1.5 text-[10px] ${
          active ? 'bg-paper/20 text-paper' : 'bg-paper-3 text-ink-3'
        }`}
      >
        {count}
      </span>
    </button>
  );
}

function OrphanRow({
  appt,
  bucket,
  selected,
  onToggle,
  clientMap,
  profileMap,
}: {
  appt: AppointmentRow;
  bucket: Bucket;
  selected: boolean;
  onToggle: () => void;
  clientMap: Map<string, { first_name: string | null; last_name: string; phone: string }>;
  profileMap: Map<string, { id: string; full_name: string; agent_id: string; role: string }>;
}) {
  const c = appt.client_id ? clientMap.get(appt.client_id) : null;
  const clientName = c
    ? [c.first_name, c.last_name].filter(Boolean).join(' ')
    : '—';
  const date = formatDateTime(new Date(appt.appointment_date), {
    dateStyle: 'short',
    timeStyle: 'short',
  });
  const apptAgent = appt.agent_id ? profileMap.get(appt.agent_id) : null;
  const clientOwner =
    c && 'assigned_agent_id' in c && (c as { assigned_agent_id?: string | null }).assigned_agent_id
      ? profileMap.get((c as { assigned_agent_id: string }).assigned_agent_id)
      : null;

  return (
    <tr className="border-b border-line hover:bg-paper-2">
      <td className="px-5 py-2.5">
        <button onClick={onToggle} className="inline-flex">
          {selected ? (
            <CheckSquare className="h-4 w-4 text-gold" />
          ) : (
            <Square className="h-4 w-4 text-ink-3" />
          )}
        </button>
      </td>
      <td className="px-2 py-2.5 text-xs text-ink-2 whitespace-nowrap">{date}</td>
      <td className="px-2 py-2.5 text-xs text-ink-2">{appt.appointment_type}</td>
      <td className="px-2 py-2.5 text-sm text-ink">
        {clientName}
        {c?.phone && (
          <span className="ms-2 text-[11px] text-ink-3">{c.phone}</span>
        )}
      </td>
      {bucket === 'maker_mismatch' && (
        <>
          <td className="px-2 py-2.5 text-xs">
            {apptAgent ? (
              <div className="inline-flex items-center gap-1.5">
                <Avatar name={apptAgent.full_name} size="sm" />
                <span>{apptAgent.full_name}</span>
              </div>
            ) : (
              <span className="text-ink-3">—</span>
            )}
          </td>
          <td className="px-2 py-2.5 text-xs">
            {clientOwner ? (
              <div className="inline-flex items-center gap-1.5">
                <Avatar name={clientOwner.full_name} size="sm" />
                <span className="text-gold">{clientOwner.full_name}</span>
              </div>
            ) : (
              <span className="text-ink-3">—</span>
            )}
          </td>
        </>
      )}
      <td className="px-2 py-2.5 text-xs text-ink-3 max-w-md truncate">{appt.notes ?? '—'}</td>
    </tr>
  );
}
